import { lazy, Suspense, useRef, useState } from "react";
import { motion, useAnimationControls } from "framer-motion";
import { ArrowUpRight, Hand } from "lucide-react";
import { EXAMPLES } from "@/content/site";
import { useUI } from "@/context/UIContext";
import { PhoneFrame, ScaledCanvas } from "./Devices";
import { SITES } from "./minisites";
import { Reveal, SectionLabel } from "./Reveal";

const ShowcaseDialog = lazy(() => import("./ShowcaseDialog"));
const pad = (n) => String(n).padStart(2, "0");

export default function Showcase() {
  const { openDemo } = useUI();
  const [index, setIndex] = useState(0);
  const [open, setOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const trigger = useRef(null);
  const controls = useAnimationControls();
  const Preview = SITES[EXAMPLES[index].key];

  const launch = async (i) => {
    trigger.current = document.activeElement;
    if (typeof i === "number") setIndex(i);
    setMounted(true);
    await controls.start({ y: -36, scale: 1.03, transition: { duration: 0.28, ease: [0.2, 0.7, 0.1, 1] } });
    setOpen(true);
    controls.start({ y: 0, scale: 1, transition: { duration: 0.01 } });
  };
  const choose = (name) => { setOpen(false); setTimeout(() => openDemo(name), 120); };

  return (
    <section id="beispiele" className="relative px-3 py-6 sm:px-5" data-testid="showcase-section">
      <div className="grain relative overflow-hidden rounded-[28px] bg-night py-20 text-zinc-100 sm:rounded-[36px] sm:py-28">
        <div className="pointer-events-none absolute left-1/2 top-0 h-[480px] w-[780px] -translate-x-1/2 rounded-full bg-gold/10 blur-[120px]" />
        <div className="wrap relative grid items-center gap-14 lg:grid-cols-[1fr_auto]">
          <div>
            <Reveal>
              <SectionLabel n="02" dark>Designbeispiele</SectionLabel>
              <h2 className="mt-5 max-w-[640px] font-display text-[34px] font-semibold leading-[1.05] tracking-[-0.03em] text-white sm:text-5xl">
                Zehn Branchen. Zehn <span className="font-serif font-normal italic text-gold">eigenständige</span> Auftritte.
              </h2>
              <p className="mt-5 max-w-[520px] text-base leading-relaxed text-zinc-300 sm:text-lg">
                Jedes Beispiel hat eigene Typografie, Bildsprache und Interaktion. Öffnen Sie das Smartphone und blättern Sie durch alle zehn Designstudien.
              </p>
            </Reveal>
            <ol className="mt-10 grid grid-cols-1 gap-x-8 sm:grid-cols-2" data-testid="showcase-list">
              {EXAMPLES.map((x, i) => (
                <li key={x.key}>
                  <button type="button" data-testid={`showcase-item-${x.key}`} onClick={() => launch(i)} onFocus={() => setIndex(i)} onMouseEnter={() => setIndex(i)}
                    className={`group flex w-full items-center gap-4 border-b border-night-line py-3.5 text-left transition-colors ${index === i ? "text-white" : "text-zinc-400"}`}>
                    <span className={`font-mono text-[12px] ${index === i ? "text-gold" : "text-zinc-400"}`}>{pad(i + 1)}</span>
                    <span className="flex-1 text-[16px] font-medium">{x.name}</span>
                    <ArrowUpRight className="h-4 w-4 opacity-60 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                  </button>
                </li>
              ))}
            </ol>
            <p className="mt-6 text-[13px] text-zinc-400">Alle Beispiele sind fiktive Designstudien – keine Kundenprojekte.</p>
          </div>

          <div className="flex flex-col items-center">
            <motion.button type="button" animate={controls} onClick={() => launch()} data-testid="showcase-phone"
              aria-label={`Beispiele öffnen – aktuell ${EXAMPLES[index].name}`} className="group relative block w-[260px] sm:w-[300px]">
              <PhoneFrame>
                <div className="pointer-events-none h-full" aria-hidden="true">
                  <ScaledCanvas base={380}><Preview /></ScaledCanvas>
                </div>
              </PhoneFrame>
              <span className="absolute inset-x-6 bottom-10 flex items-center justify-center gap-2 rounded-full bg-white/90 py-3 text-[14px] font-semibold text-ink shadow-xl backdrop-blur transition-transform group-hover:-translate-y-1 group-active:scale-95">
                <Hand className="h-4 w-4" /> Beispiele öffnen
              </span>
            </motion.button>
            <p className="mt-6 font-mono text-[12px] text-zinc-400" aria-live="polite">{pad(index + 1)} / 10 · {EXAMPLES[index].name}</p>
          </div>
        </div>
      </div>
      {mounted && (
        <Suspense fallback={null}>
          <ShowcaseDialog open={open} onOpenChange={setOpen} index={index} setIndex={setIndex} onChoose={choose} returnFocus={trigger} />
        </Suspense>
      )}
    </section>
  );
}
